import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";

import { useAuth } from "../context/AuthContext";

import StandardScreen, {
  ScreenContent,
} from "../components/layout/StandardScreen";
import Stack from "../components/layout/Stack";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";

import {
  getPreauthSession,
  clearPreauthSession,
} from "../components/helpers/authFlowStorage";

const shellStyle = {
  maxWidth: "480px",
  margin: "0 auto",
  paddingTop: "2rem",
};

const backLinkStyle = {
  border: 0,
  padding: 0,
  background: "transparent",
  color: "var(--color-accent)",
  font: "inherit",
  fontWeight: "var(--font-weight-semibold)",
  cursor: "pointer",
};

const ExcursionGate = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { applySession } = useAuth();

  const { phoneNumber, preauthToken } = getPreauthSession();

  const [checking, setChecking] = useState(true);
  const [hasRealExcursion, setHasRealExcursion] = useState(false);
  const [isContinuing, setIsContinuing] = useState(false);
  const [loginCompleted, setLoginCompleted] = useState(false);

  useEffect(() => {
    if (loginCompleted) return;

    if (!phoneNumber || !preauthToken) {
      navigate("/login", { replace: true });
    }
  }, [phoneNumber, preauthToken, navigate, loginCompleted]);

  useEffect(() => {
    const run = async () => {
      if (!phoneNumber || loginCompleted) return;

      try {
        setChecking(true);

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/auth/has-real-excursion`,
          {
            params: { phone_number: phoneNumber },
          }
        );

        setHasRealExcursion(Boolean(res.data?.has_real_excursion));
      } catch (error) {
        const msg =
          error.response?.data?.detail ||
          error.response?.data?.message ||
          "";
        const code = error?.response?.status;

        if (
          code === 401 ||
          msg === "Invalid or expired pre-auth token" ||
          msg === "Invalid pre-auth token type" ||
          msg === "Invalid pre-auth token subject" ||
          msg === "Phone mismatch"
        ) {
          clearPreauthSession();
          alert(t("excursion_gate.errors.session_expired"));
          navigate("/login", { replace: true });
          return;
        }

        setHasRealExcursion(false);
      } finally {
        setChecking(false);
      }
    };

    run();
  }, [phoneNumber, preauthToken, navigate, loginCompleted, t]);

  const goRequestJoin = () => {
    navigate("/excursion-join", { replace: true });
  };

  const goCreateExcursion = () => {
    navigate("/excursion-create", { replace: true });
  };

  const goToExistingExcursion = async () => {
    if (checking || isContinuing || loginCompleted) return;

    try {
      setIsContinuing(true);

      const subsRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/auth/matching-subscriptions`,
        {
          params: { phone_number: phoneNumber },
        }
      );

      const subscriptions = Array.isArray(subsRes.data) ? subsRes.data : [];

      if (subscriptions.length === 0) {
        alert(t("excursion_gate.errors.no_subscription"));
        return;
      }

      if (subscriptions.length === 1) {
        const sub = subscriptions[0];

        const finalRes = await axios.post(
          `${process.env.REACT_APP_API_URL}/auth/login-final`,
          {
            app_id: sub.app_id,
            preauth_token: preauthToken,
          }
        );

        const accessToken = finalRes.data?.access_token;
        const subscriptionStatus = String(
          finalRes.data?.subscription_status || ""
        ).toLowerCase();
        const finalRole = finalRes.data?.role || sub.role || "";

        if (!accessToken) {
          throw new Error("No access token returned");
        }

        await applySession({
          token: accessToken,
          role: finalRole,
          activeAppId: sub.app_id,
          subscriptionStatus,
        });

        setLoginCompleted(true);

        if (subscriptionStatus === "pending") {
          navigate("/pending", { replace: true });
          return;
        }

        navigate("/my-summary", { replace: true });
        return;
      }

      navigate("/excursion-goto", { replace: true });
    } catch (error) {
      const msg =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        t("excursion_gate.errors.continue_failed");

      if (
        error?.response?.status === 401 ||
        msg === "Invalid or expired pre-auth token" ||
        msg === "Invalid pre-auth token type" ||
        msg === "Invalid pre-auth token subject" ||
        msg === "Phone mismatch"
      ) {
        clearPreauthSession();
        alert(t("excursion_gate.errors.session_expired"));
        navigate("/login", { replace: true });
        return;
      }

      alert(msg);
    } finally {
      setIsContinuing(false);
    }
  };

  const goBack = () => {
    clearPreauthSession();
    navigate("/login", {
      replace: true,
      state: { phoneNumber },
    });
  };

  if (!phoneNumber || !preauthToken) return null;

  return (
    <StandardScreen background="default">
      <ScreenContent>
        <div style={shellStyle}>
          <Card
            title={t("excursion_gate.title")}
            subtitle={t("excursion_gate.subtitle")}
          >
            <Stack gap="4">
              <Button
                onClick={goToExistingExcursion}
                disabled={
                  checking ||
                  isContinuing ||
                  loginCompleted ||
                  !hasRealExcursion
                }
                fullWidth
                size="lg"
              >
                {checking
                  ? t("excursion_gate.checking")
                  : isContinuing
                  ? t("excursion_gate.continuing")
                  : t("excursion_gate.go_existing")}
              </Button>

              <Button
                onClick={goRequestJoin}
                fullWidth
                size="lg"
                variant="secondary"
              >
                {t("excursion_gate.join")}
              </Button>

              <Button
                onClick={goCreateExcursion}
                fullWidth
                size="lg"
                variant="secondary"
              >
                {t("excursion_gate.create")}
              </Button>

              <div style={{ textAlign: "center" }}>
                <button
                  type="button"
                  onClick={goBack}
                  style={backLinkStyle}
                >
                  {t("excursion_gate.back")}
                </button>
              </div>
            </Stack>
          </Card>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
};

export default ExcursionGate;